import setBuildNumber from '~/_base/functions/setBuildNumber';
import styles from './stylesheets/default.scss';
import siteAssistantStyles from './stylesheets/siteAssistant/default.scss';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import AuthenticatedApp from './authenticatedApp';
import UnauthenticatedApp from './unauthenticatedApp';
import AppSendEmail from './modules/sendEmail/appSendEmail';
import configureStore from './store/configureStore';
import isNil from '~/_base/functions/isNil';
import InsightsChatView from '~/_base/components/insightsChatView';
import ApeHostNotifications from '~/_base/functions/apeHostNotifications';
import qs from 'qs';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { GlobalTheme } from '../_base/apeMuiTheme';
import { ToastProvider } from '~/_base/context/toastContext';
import { SWRConfig } from 'swr';
import TenantNotFound from './modules/tenantNotFound';
import FindYourAPEUrl from './modules/findYourAPEUrl';
import SignInContainer from './modules/signIn/signInContainer';
import SignIn from './modules/signIn/signIn';
import RailsNotification from '~/_base/components/railsNotification';
import RequestPasswordReset from './modules/signIn/requestPasswordReset';
import EnterNewPassword from './modules/signIn/enterNewPassword';
import CompleteSetup from './modules/signIn/completeSetup';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Cookie from 'js-cookie';
import { datadogLogs } from '@datadog/browser-logs';
import packfile from '~/package.json';
require('./i18n');
require('~/_base/styles/_apeMuiTheme.scss');

datadogLogs.init({
  clientToken: 'pubda469b8d1fc86ef7f1e764da693ea30d',
  site: 'datadoghq.eu',
  sessionSampleRate: 100,
  service: 'samm-frontend',
  version: packfile.version,
  env: process.env.NODE_ENV === 'development' ? 'develop' : 'production',
  // Let Airbrake handle errors
  forwardErrorsToLogs: false,
});

document.addEventListener('DOMContentLoaded', function() {
  initialize();

  // Hide the Zendesk floating help button. We programmatically open this
  // from the left side nav
  if (window.zE != null) {
    window.zE('webWidget', 'hide');
    window.zE('webWidget:on', 'close', () => {
      window.zE('webWidget', 'hide');
    });
  }
});

// Historically everything was thrown into this entrypoint, hence why we still
// have a lot of unnecessary stuff being rendered here. Ideally most of these
// pages get separated into their own entrypoints in the future because they're
// just adding extra bundle size
function initialize() {
  const container = document.getElementsByClassName('container')[0];
  if (container != null) {
    container.style.display = 'none';
  }
  const spinner = document.getElementById('spinner');
  if (spinner != null) {
    spinner.style.display = 'none';
  }
  const store = configureStore();

  const signInElement = document.getElementById('ape_signin_wonderland');
  if (signInElement != null) {
    createRoot(signInElement).render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <ToastProvider>
            <SignInContainer>
              <RailsNotification />
              <Suspense fallback="Loading...">
                <SignIn />
              </Suspense>
            </SignInContainer>
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
    return;
  }

  const forgotPasswordElement = document.getElementById('ape_password_reset');
  if (forgotPasswordElement != null) {
    createRoot(forgotPasswordElement).render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <ToastProvider>
            <RailsNotification />
            <SignInContainer>
              <RailsNotification />
              <RequestPasswordReset />
              <EnterNewPassword />
              <CompleteSetup />
            </SignInContainer>
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
    return;
  }

  const tenantNotFoundElement = document.getElementById(
    'react-tenant-not-found'
  );
  if (tenantNotFoundElement != null) {
    createRoot(tenantNotFoundElement).render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <ToastProvider>
            <SignInContainer>
              <RailsNotification />
              <TenantNotFound />
            </SignInContainer>
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
    return;
  }

  const findYourAPEUrlElement = document.getElementById('react-go-login');
  if (findYourAPEUrlElement != null) {
    createRoot(findYourAPEUrlElement).render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <ToastProvider>
            <SignInContainer>
              <RailsNotification />
              <FindYourAPEUrl />
            </SignInContainer>
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
    return;
  }

  const rootElement = document.getElementById('ape_site_injection');
  if (!isNil(rootElement)) {
    const authenticated = Cookie.get('remember_token');
    createRoot(rootElement).render(
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={GlobalTheme}>
            <LocalizationProvider dateAdapter={AdapterMoment}> {/* Updated provider */}
              <ToastProvider>
                <SWRConfig
                  value={{
                    revalidateOnFocus: false,
                  }}
                >
                  {authenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />}
                </SWRConfig>
              </ToastProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Provider>
    );
  }

  const reactSendEmailElement = document.getElementById('react-send-email');
  if (!isNil(reactSendEmailElement)) {
    createRoot(reactSendEmailElement).render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={GlobalTheme}>
          <ToastProvider>
            <AppSendEmail />
          </ToastProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  const reactSiteAssistantElement = document.getElementById(
    'react-site-assistant'
  );
  if (!isNil(reactSiteAssistantElement)) {
    const queryString = (document.location.search || '').slice(1);
    const params = qs.parse(queryString);
    const platform = params['platform'] || '';
    let embedded = params.embed == 'true';

    if (embedded) {
      reactSiteAssistantElement.classList.add('embedded');
    }

    if (platform) {
      if (platform == 'uwp') {
        ApeHostNotifications.pushNotifier(e =>
          window.external.notify(JSON.stringify(e))
        );
      }

      // normally this gets added by ApeEnvironment.js from the android client, but because this is
      // getting loaded direct from the web, its not available, so we need to add the notifier manually
      ApeHostNotifications.pushNotifier(e =>
        console.log('ApeHostNotification:' + JSON.stringify(e))
      );
    }

    createRoot(reactSiteAssistantElement).render(
      <InsightsChatView embedded={embedded} platform={platform} />
    );
  }
}
